import React, { useEffect, useState } from 'react';
import { Tour, Button, Tooltip } from 'antd';
import type { TourProps } from 'antd';
import { QuestionCircleOutlined } from '@ant-design/icons';
import { useIsMobile } from '../hooks/useIsMobile';

export interface OnboardingStep {
    title: React.ReactNode;
    description: React.ReactNode;
    /** CSS selector of the element to spotlight; missing/unmatched → centered card. */
    selector?: string;
    placement?: TourProps['placement'];
    /** Runs when the step becomes current, e.g. to switch the tab holding the target. */
    onEnter?: () => void;
    /** Runs when the tour moves off this step or closes — undo whatever onEnter opened. */
    onLeave?: () => void;
    /** false keeps the page undimmed, for steps that show a popup above the tour mask. */
    mask?: boolean;
}

export interface OnboardingTourProps {
    /** localStorage key marking the tour as seen; bump the suffix to re-show after a rewrite. */
    storageKey: string;
    steps: OnboardingStep[];
}

const readSeen = (key: string): boolean => {
    try {
        return window.localStorage.getItem(key) === '1';
    } catch {
        return false;
    }
};

const markSeen = (key: string) => {
    try {
        window.localStorage.setItem(key, '1');
    } catch {
        // Private mode / blocked storage: the tour just shows again next visit.
    }
};

/**
 * First-visit walkthrough for a trading page. Opens by itself once per browser,
 * and renders a "Guide" button so the user can replay it at any time.
 */
const OnboardingTour: React.FC<OnboardingTourProps> = ({ storageKey, steps }) => {
    const isMobile = useIsMobile();
    const [open, setOpen] = useState(false);
    const [current, setCurrent] = useState(0);

    useEffect(() => {
        if (isMobile || readSeen(storageKey)) return;
        // Let the split panes and the config form lay out before measuring targets.
        const timer = window.setTimeout(() => start(), 800);
        return () => window.clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [storageKey, isMobile]);

    const goTo = (index: number) => {
        if (open) steps[current]?.onLeave?.();
        steps[index]?.onEnter?.();
        setCurrent(index);
    };

    const start = () => {
        goTo(0);
        setOpen(true);
    };

    const close = () => {
        steps[current]?.onLeave?.();
        setOpen(false);
        markSeen(storageKey);
    };

    // Targets are resolved on every render so a step whose element only mounts
    // after onEnter (a lazily rendered tab) still finds it.
    const tourSteps: TourProps['steps'] = steps.map((step) => ({
        title: step.title,
        description: step.description,
        placement: step.placement,
        mask: step.mask,
        target: step.selector
            ? () => document.querySelector(step.selector!) as HTMLElement
            : null,
    }));

    // The compact shell keeps these targets in drawers and sheets, so the tour is desktop-only.
    if (isMobile) return null;

    return (
        <>
            <Tooltip title="Show the guide for this page">
                <Button
                    size="small"
                    type="text"
                    icon={<QuestionCircleOutlined />}
                    onClick={start}
                    style={{ color: '#848E9C', fontSize: 12 }}
                >
                    Guide
                </Button>
            </Tooltip>
            <Tour
                open={open}
                current={current}
                onChange={goTo}
                onClose={close}
                onFinish={close}
                steps={tourSteps}
                zIndex={1100}
                rootClassName="onboarding-tour"
                indicatorsRender={(cur, total) => (
                    <span style={{ fontSize: 12, color: '#848E9C' }}>{cur + 1} / {total}</span>
                )}
            />
            {/* Dark card in the app's palette; antd's primary variant floods it green. */}
            <style>{`
                .onboarding-tour .ant-tour-content { max-width: 380px; }
                .onboarding-tour .ant-tour-inner { background: #1E2329; border: 1px solid #2B3139; box-shadow: 0 12px 32px rgba(0, 0, 0, 0.55); }
                .onboarding-tour .ant-tour-arrow::before { background: #1E2329; }
                .onboarding-tour .ant-tour-title { color: #EAECEF; font-weight: 600; }
                .onboarding-tour .ant-tour-description { color: #B7BDC6; font-size: 13px; line-height: 1.6; }
                .onboarding-tour .ant-tour-description b { color: #0ECB81; font-weight: 600; }
                .onboarding-tour .ant-tour-close { color: #848E9C; }
                .onboarding-tour .ant-tour-prev-btn { background: transparent; border-color: #3d4148; color: #B7BDC6; }
            `}</style>
        </>
    );
};

export default OnboardingTour;
